
export default {
    data() {
        return {
            cardDetails: {
                cardNumber: '',
                expiryDate: '',
                cvv: '',
                postalCode: '',
            },
            paymentProfile: [],
            selectedProfile: '',
            errors: {},
            cvvMaxLength: 3,
            cvvPlaceholder: 'CVV',
            selectedTestCase: '',
            testCases: [
                {
                    id: 'success',
                    label: 'Successful Transaction',
                    cardNumber: '4111 1111 1111 1111',
                    cvv: '123',
                    postalCode: '12345',
                    description: 'Will process successfully with any valid CVV and zip code'
                },
                {
                    id: 'declined',
                    label: 'Declined Transaction',
                    cardNumber: '4111 1111 1111 1111',
                    cvv: '123',
                    postalCode: '46282',
                    description: 'Transaction will be declined'
                }
            ],
            cardTypes: [
                {
                    name: 'Visa',
                    pattern: /^4[0-9]{12}(?:[0-9]{3})?$/,
                    cvvLength: 3
                },
                {
                    name: 'Mastercard',
                    pattern: /^5[1-5][0-9]{14}$/,
                    cvvLength: 3
                },
                {
                    name: 'American Express',
                    pattern: /^3[47][0-9]{13}$/,
                    cvvLength: 4
                },
                {
                    name: 'Discover',
                    pattern: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
                    cvvLength: 3
                }
            ]
        };
    },
    computed: {
        distinctPaymentProfiles() {
            const seen = new Set();
            return this.paymentProfile.filter(profile => {
                const identifier = `${profile.type}-${profile.card}`;
                if (seen.has(identifier)) {
                    return false;
                }
                seen.add(identifier);
                return true;
            });
        },
    },
    async mounted() {
        const payment_profiles = await this.$member.cart.getPaymentProfiles();
        this.paymentProfile = payment_profiles || [];
        this.loadAcceptJs();
    },
    methods: {
        async waitForAccept(retries = 2, delay = 1000) {
            for (let i = 0; i < retries; i++) {
                if (window.Accept) {
                    return true;
                }

                console.log(`Accept.js not found, attempt ${i + 1} of ${retries}`);
                this.loadAcceptJs();

                // Wait for specified delay
                await new Promise(resolve => setTimeout(resolve, delay));
            }

            return false;
        },
        loadAcceptJs() {
            // Only load script on client side
            if (process.client && typeof document !== 'undefined') {
                if (!document.querySelector(`script[src="${this.$config.anetAcceptJs}"]`)) {
                    const script = document.createElement('script');
                    script.src = this.$config.anetAcceptJs;
                    script.defer = true;
                    script.onload = () => console.log('Accept.js loaded successfully');
                    script.onerror = (error) => {
                        console.error('Failed to load Accept.js:', error);
                        // You might want to set some state here to track failed loads
                    };
                    document.head.appendChild(script);
                }
            }
        },
        validatePostalCode() {
            // Clear any previous postal code error first
            this.errors = { ...this.errors, postalCode: null };

            // Get the current postal code value and remove any spaces
            const postalCode = this.cardDetails.postalCode.trim();

            // Check if empty
            if (!postalCode) {
                this.errors.postalCode = 'Postal code is required';
                return false;
            }

            // US ZIP code validation (5 digits or 5+4)
            const zipRegex = /^\d{5}(-\d{4})?$/;
            if (!zipRegex.test(postalCode)) {
                this.errors.postalCode = 'Please enter a valid ZIP code';
                return false;
            }

            return true;
        },
        onProfileSelect() {
            if (this.selectedProfile) {
                this.cardDetails = {
                    cardNumber: '',
                    expiryDate: '',
                    cvv: '',
                    postalCode: '',
                };
                this.errors = {};
            }
        },
        formatCardNumber() {
            const value = this.cardDetails.cardNumber.replace(/\D/g, '');
            this.cardDetails.cardNumber = value.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
            this.validateCardNumber();
        },
        validateCardNumber() {
            const cardNumber = this.cardDetails.cardNumber.replace(/\s/g, '');

            // Check if empty
            if (!cardNumber) {
                this.errors.cardNumber = 'Card number is required';
                return false;
            }

            // Check if contains only numbers
            if (!/^\d+$/.test(cardNumber)) {
                this.errors.cardNumber = 'Card number can only contain digits';
                return false;
            }

            // Detect card type
            const cardType = this.cardTypes.find(type => type.pattern.test(cardNumber));
            if (!cardType) {
                this.errors.cardNumber = 'Invalid card number or unsupported card type';
                return false;
            }

            // Update CVV length based on card type
            this.cvvMaxLength = cardType.cvvLength;
            this.cvvPlaceholder = `CVV (${cardType.cvvLength} digits)`;

            // Validate using Luhn algorithm
            if (!this.luhnCheck(cardNumber)) {
                this.errors.cardNumber = 'Invalid card number checksum';
                return false;
            }

            this.errors.cardNumber = null;
            return true;
        },
        luhnCheck(cardNumber) {
            let sum = 0;
            let isEven = false;

            // Loop through values starting from the rightmost digit
            for (let i = cardNumber.length - 1; i >= 0; i--) {
                let digit = parseInt(cardNumber.charAt(i), 10);

                if (isEven) {
                    digit *= 2;
                    if (digit > 9) {
                        digit -= 9;
                    }
                }

                sum += digit;
                isEven = !isEven;
            }

            return (sum % 10) === 0;
        },
        validateCVV() {
            const cvv = this.cardDetails.cvv;

            // Check if empty
            if (!cvv) {
                this.errors.cvv = 'CVV is required';
                return false;
            }

            // Check if contains only numbers
            if (!/^\d+$/.test(cvv)) {
                this.errors.cvv = 'CVV can only contain digits';
                return false;
            }

            // Check length based on card type
            if (cvv.length !== this.cvvMaxLength) {
                this.errors.cvv = `CVV must be ${this.cvvMaxLength} digits`;
                return false;
            }

            this.errors.cvv = null;
            return true;
        },
        formatExpiryDate() {
            const value = this.cardDetails.expiryDate.replace(/\D/g, '');
            if (value.length <= 2) {
                this.cardDetails.expiryDate = value;
            } else {
                this.cardDetails.expiryDate = `${value.slice(0, 2)}/${value.slice(2, 4)}`;
            }
        },
        validateExpiryDate() {
            const [month, year] = this.cardDetails.expiryDate.split('/').map(Number);
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear() % 100;
            const currentMonth = currentDate.getMonth() + 1;

            // Check if empty or invalid format
            if (!month || !year || isNaN(month) || isNaN(year)) {
                this.errors.expiryDate = 'Invalid expiration date format (MM/YY)';
                return false;
            }

            // Check month range
            if (month < 1 || month > 12) {
                this.errors.expiryDate = 'Invalid month (1-12)';
                return false;
            }

            // Check if card is expired
            if (year < currentYear || (year === currentYear && month < currentMonth)) {
                this.errors.expiryDate = 'Card has expired';
                return false;
            }

            // Check if date is too far in the future (optional, adjust as needed)
            if (year > currentYear + 20) {
                this.errors.expiryDate = 'Expiration date too far in the future';
                return false;
            }

            this.errors.expiryDate = null;
            return true;
        },

        formatExpiryDate() {
            let value = this.cardDetails.expiryDate.replace(/\D/g, '');

            // Format as MM/YY
            if (value.length >= 2) {
                const month = value.slice(0, 2);
                const year = value.slice(2, 4);

                // Validate month input immediately
                if (parseInt(month) > 12) {
                    value = '12' + year;
                }

                this.cardDetails.expiryDate = `${value.slice(0, 2)}/${value.slice(2, 4)}`;
            } else {
                this.cardDetails.expiryDate = value;
            }
        },

        validateAllFields() {
            const isCardValid = this.validateCardNumber();
            const isCvvValid = this.validateCVV();
            const isExpiryValid = this.validateExpiryDate();
            const isPostalValid = this.validatePostalCode();

            return isCardValid && isCvvValid && isExpiryValid && isPostalValid;
        },
        async submitCard() {
            // Clear all previous errors
            this.errors = {};

            if (this.selectedProfile) {
                this.$emit('submitCheckoutWithCreditCard', this.selectedProfile);
                return;
            }

            // Validate all fields before submission
            if (!this.validateAllFields()) {
                return;
            }

            const authData = {
                clientKey: this.$config.anetClientKey,
                apiLoginID: this.$config.anetLoginId,
            };

            const cardData = {
                cardNumber: this.cardDetails.cardNumber.replace(/\s/g, ''),
                month: this.cardDetails.expiryDate.split('/')[0],
                year: `20${this.cardDetails.expiryDate.split('/')[1]}`,
                cardCode: this.cardDetails.cvv,
                zip: this.cardDetails.postalCode,
            };

            if (!window.Accept) {
                // Try to load Accept.js again with retries
                const acceptLoaded = await this.waitForAccept(2, 1000);

                if (!acceptLoaded) {
                    this.errors.submitError = 'Payment system could not be initialized after multiple attempts';
                    return;
                }
            }

            window.Accept.dispatchData({ authData, cardData }, this.responseHandler);
        },
        responseHandler(response) {
            if (response.messages.resultCode === 'Ok') {
                this.errors = {};
                this.$emit('submitCheckoutWithCreditCard', response.opaqueData);
            } else {
                this.errors.submitError = response.messages.message[0].text;
            }
        },
        fillTestData() {
            if (!this.selectedTestCase) {
                this.cardDetails = {
                    cardNumber: '4111 1111 1111 1111',
                    expiryDate: this.getValidExpiryDate(),
                    cvv: '123',
                    postalCode: '12345'
                };
            } else {
                const testCase = this.testCases.find(tc => tc.id === this.selectedTestCase);
                if (testCase) {
                    this.cardDetails = {
                        cardNumber: testCase.cardNumber,
                        expiryDate: this.getValidExpiryDate(),
                        cvv: testCase.cvv,
                        postalCode: testCase.postalCode
                    };
                }
            }
            this.errors = {};
        },
        getValidExpiryDate() {
            const currentDate = new Date();
            const month = String(currentDate.getMonth() + 1).padStart(2, '0');
            const year = String((currentDate.getFullYear() + 1) % 100);
            return `${month}/${year}`;
        },
        showTestCreditCardButton() {
            return this.$config.env !== 'production' && this.$config.env !== 'beta';
        }
    }
};
